import { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import axios from "../../../api/axiosInstance";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { readStoredUser } from "../../../utils/sessionUser";
import useCrossTabDataRefreshToken from "../../../hooks/useCrossTabDataRefresh";
import useItemDialogFocus from "../useItemDialogFocus";
import { getUnitName } from "../../utils/unitNames";
import EditDeliveryNotePopup from "./EditDeliveryNotePopup";
import { priceConfirmationText } from "./priceConfirmationMessages";
import "../../PurchasingUi.css";
import "./PriceConfirmationInbox.css";

const PAGE_SIZE = 25;
const statuses = { draft: "Draft", received: "Received", verified: "Verified", verified_with_differences: "Verified with differences", closed: "Closed" };
const origins = { manual: "Manual estimate", last_purchase: "Last purchase", tender: "Tender" };
const canViewPrices = (user) => Number(user?.id) === 1 || user?.permissions?.access_settings === true || user?.permissions?.["purchasing.delivery_notes.view_tab"] === true;

export default function PriceConfirmationInbox({ businessUnitId, onClose, onChanged }) {
  const [, refreshSession] = useState(0);
  useEffect(() => {
    const refresh = () => refreshSession((value) => value + 1);
    window.addEventListener("palantir-session-updated", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("palantir-session-updated", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);
  const user = readStoredUser();
  if (!canViewPrices(user) || !businessUnitId) return null;
  return <InboxSession key={`${user?.id}:${businessUnitId}:${user?.active_business_unit_id}`} businessUnitId={businessUnitId}
    actorId={user?.id} onClose={onClose} onChanged={onChanged} />;
}

function InboxSession({ businessUnitId, actorId, onClose, onChanged }) {
  const { languageId, themeId } = usePmsTheme();
  const t = (key) => priceConfirmationText(languageId, key);
  const locale = languageId === "es" ? "es-ES" : languageId === "ca" ? "ca-ES" : "en-GB";
  const titleId = useId();
  const [draftSearch, setDraftSearch] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [revision, setRevision] = useState(0);
  const [response, setResponse] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const refreshToken = useCrossTabDataRefreshToken();
  const requestKey = JSON.stringify([businessUnitId, actorId, search, status, page, revision, refreshToken]);
  const currentResponse = response?.key === requestKey ? response : null;
  const loading = !currentResponse;
  const data = currentResponse?.data;
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const total = Number(data?.total || 0);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const dialogRef = useItemDialogFocus(!selectedNote, false, onClose);

  useEffect(() => {
    if (selectedNote) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    const sessionMatches = () => {
      const user = readStoredUser();
      return canViewPrices(user) && String(user?.id) === String(actorId) && String(user?.active_business_unit_id) === String(businessUnitId);
    };
    if (!sessionMatches()) { setSessionExpired(true); return undefined; }
    axios.get("/api/purchasing/delivery-notes-direct/prices-to-confirm", {
      params: { business_unit_id: businessUnitId, search, status, page, page_size: PAGE_SIZE },
      headers: { "X-Pilcore-Business-Unit-Id": String(businessUnitId) },
      pilcoreExpectedUserId: actorId, signal: controller.signal,
    }).then(({ data: result }) => {
      if (cancelled) return;
      if (!sessionMatches()) { setSessionExpired(true); return; }
      // A saved/deleted line can empty the last page; return to the last valid one.
      const lastPage = Math.max(1, Math.ceil(Number(result?.total || 0) / PAGE_SIZE));
      if (page > lastPage) { setPage(lastPage); return; }
      setResponse({ key: requestKey, data: result });
    }).catch(() => {
      if (!cancelled) {
        if (!sessionMatches()) setSessionExpired(true);
        else setResponse({ key: requestKey, error: true });
      }
    });
    return () => { cancelled = true; controller.abort(); };
  }, [businessUnitId, actorId, search, status, page, requestKey, selectedNote]);

  const number = (value, maximumFractionDigits = 4) => value == null || value === "" || !Number.isFinite(Number(value))
    ? "—" : new Intl.NumberFormat(locale, { maximumFractionDigits }).format(Number(value));
  const money = (value, currency, maximumFractionDigits = 8) => {
    if (value == null || value === "" || !Number.isFinite(Number(value))) return "—";
    if (!/^[A-Z]{3}$/.test(currency || "")) return `${number(value, maximumFractionDigits)} ${currency || ""}`.trim();
    return new Intl.NumberFormat(locale, { style: "currency", currency, maximumFractionDigits }).format(Number(value));
  };
  const date = (value) => {
    const parsed = new Date(`${String(value || "").slice(0, 10)}T12:00:00`);
    return Number.isFinite(parsed.getTime()) ? new Intl.DateTimeFormat(locale).format(parsed) : "—";
  };
  const openNote = (row) => {
    const user = readStoredUser();
    if (!canViewPrices(user) || String(user?.id) !== String(actorId) || String(user?.active_business_unit_id) !== String(businessUnitId)) {
      setSessionExpired(true); return;
    }
    setSelectedNote(row.delivery_note_id);
  };
  if (selectedNote && !sessionExpired) return <EditDeliveryNotePopup isOpen deliveryNoteId={selectedNote} onClose={() => {
    setSelectedNote(null); setRevision((value) => value + 1); onChanged?.();
  }} />;

  return createPortal(<div className="pilcore-purchasing-overlay price-inbox" data-theme={themeId}
    onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="price-inbox__dialog" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
      <header className="price-inbox__header"><div><h2 id={titleId}>{t("Prices to confirm")}</h2>
        <p>{t("Open the delivery note to correct or confirm its prices. Reference prices remain estimates until confirmed.")}</p></div>
        <button type="button" className="price-inbox__close" onClick={onClose} aria-label={t("Close")}>×</button>
      </header>
      {sessionExpired ? <div className="price-inbox__notice" role="alert">{t("The active business unit has changed. Close this inbox and reopen it from the current workspace.")}</div> : <>
        <div className="price-inbox__toolbar">
          <form role="search" onSubmit={(event) => { event.preventDefault(); setSearch(draftSearch.trim()); setPage(1); }}>
            <input type="search" maxLength={200} aria-label={t("Search item, code, supplier or delivery note")}
              placeholder={t("Search item, code, supplier or delivery note")} value={draftSearch} onChange={(event) => setDraftSearch(event.target.value)} />
            <button type="submit">{t("Search")}</button>
          </form>
          <div className="price-inbox__filters" role="group" aria-label={t("Price status / source")}>
            {[["all", "All"], ["missing", "Missing price"], ["estimated", "Estimated"]].map(([value, label]) =>
              <button type="button" key={value} aria-pressed={status === value} onClick={() => { setStatus(value); setPage(1); }}>
                {t(label)}{data?.counts && <span>{number(data.counts[value], 0)}</span>}
              </button>)}
          </div>
          <button type="button" onClick={() => setRevision((value) => value + 1)} disabled={loading}>{t("Refresh")}</button>
        </div>
        <div className="price-inbox__body" aria-busy={loading}>
          {loading ? <p className="price-inbox__notice" role="status">{t("Loading prices…")}</p>
            : currentResponse.error ? <div className="price-inbox__notice" role="alert"><p>{t("Could not load pending prices.")}</p>
              <button type="button" onClick={() => setRevision((value) => value + 1)}>{t("Try again")}</button></div>
              : !rows.length ? <div className="price-inbox__notice" role="status"><p>{t(search || status !== "all" ? "No articles match these filters." : "No prices to confirm.")}</p>
                {(search || status !== "all") && <button type="button" onClick={() => { setSearch(""); setDraftSearch(""); setStatus("all"); setPage(1); }}>{t("Clear filters")}</button>}
              </div> : <table className="price-inbox__table">
                <caption className="price-inbox__srOnly">{t("Pending article prices")}</caption>
                <thead><tr>{["Item", "Delivery note", "Supplier", "Quantity / unit", "Provisional unit price", "Price status / source", "Provisional line total", "Action"].map((heading) => <th scope="col" key={heading}>{t(heading)}</th>)}</tr></thead>
                <tbody>{rows.map((row) => <tr key={row.line_id}>
                  <td data-label={t("Item")}><strong>{row.item_name || "—"}</strong><small>{row.item_code || "—"}</small></td>
                  <td data-label={t("Delivery note")}><button type="button" className="price-inbox__link" onClick={() => openNote(row)}>{row.delivery_note_number || `#${row.delivery_note_id}`}</button>
                    <small>{date(row.delivery_date)} · {t(statuses[row.delivery_note_status] || row.delivery_note_status)}</small></td>
                  <td data-label={t("Supplier")}>{row.supplier_name || "—"}</td>
                  <td data-label={t("Quantity / unit")} className="price-inbox__numeric">{number(row.quantity)}<small>{getUnitName({ name: row.unit_name, name_translations: row.unit_name_translations }, languageId) || t("Unit not set")}</small></td>
                  <td data-label={t("Provisional unit price")} className="price-inbox__numeric">{row.price_status === "missing" ? "—" : money(row.unit_price, row.currency)}</td>
                  <td data-label={t("Price status / source")}><span className="price-inbox__badge">{t(row.price_status === "missing" ? "Missing price" : "Estimated")}</span>
                    {row.price_status === "estimated" && <small>{t(origins[row.price_estimate_source] || "Manual estimate")}</small>}</td>
                  <td data-label={t("Provisional line total")} className="price-inbox__numeric">{row.price_status === "missing" || row.unit_price == null || row.quantity == null ? "—" : money(Number(row.quantity) * Number(row.unit_price), row.currency, 2)}</td>
                  <td><button type="button" aria-label={`${t("Open delivery note")}: ${row.delivery_note_number || row.delivery_note_id} · ${row.item_name}`} onClick={() => openNote(row)}>{t("Open delivery note")}</button></td>
                </tr>)}</tbody>
              </table>}
        </div>
        <footer className="price-inbox__footer"><p>{t("Missing prices are not zero-cost purchases. Confirmed and complimentary lines are excluded.")}</p>
          {data && <div className="price-inbox__pagination"><span>{number(total, 0)} {t("article lines")}</span>
            <div><button type="button" disabled={loading || page <= 1} onClick={() => setPage((value) => value - 1)}>{t("Previous")}</button>
              <span>{t("Page")} {page} {t("of")} {totalPages}</span>
              <button type="button" disabled={loading || page >= totalPages} onClick={() => setPage((value) => value + 1)}>{t("Next")}</button></div>
          </div>}
        </footer>
      </>}
    </section>
  </div>, document.body);
}
